import { createSlice } from '@reduxjs/toolkit';
import { getExpenseAllocationAction } from 'redux/action/expenseGroupAllocationAction';
import { getOneOffExpenseAllocationAction } from 'redux/action/oneOffAction';

const initialState = {
	// Expense allocation data
	allocationData: [] as any[],
	allocationLoading: false,
	allocationError: null as any,
	grandTotal: 0,
	// Pagination state
	pagination: {
		currentPage: 1,
		totalPages: 0,
		totalRecords: 0,
		limit: 20,
	},
	// Infinite scroll state
	hasMore: true,
	isLoadingMore: false,
	firstTimeFetchLoading: false,
};

const expenseAllocationSlice = createSlice({
	name: 'expenseAllocation',
	initialState,
	reducers: {
		clearAllocationData: (state) => {
			state.allocationData = [];
			state.allocationError = null;
			state.grandTotal = 0;
			state.pagination = {
				currentPage: 1,
				totalPages: 0,
				totalRecords: 0,
				limit: 20,
			};
			state.hasMore = true;
			state.isLoadingMore = false;
			state.firstTimeFetchLoading = false;
		},
		resetAllocationList: (state) => {
			state.allocationData = [];
			state.pagination.currentPage = 1;
			state.hasMore = true;
		},
	},
	extraReducers: (builder) => {
		// Get Expense Allocation Data
		builder.addCase(getExpenseAllocationAction.pending, (state, action) => {
			const page = action.meta.arg.page || 1;
			if (page === 1) {
				// First page - initial load
				state.firstTimeFetchLoading = true;
				state.allocationLoading = true;
			} else {
				// Subsequent pages - infinite scroll
				state.isLoadingMore = true;
			}
			state.allocationError = null;
		});
		builder.addCase(getExpenseAllocationAction.fulfilled, (state, action) => {
			const page = action.meta.arg.page || 1;
			const newData = action.payload?.data?.data || [];

			if (page === 1) {
				// First page - replace all data
				state.allocationData = newData;
				state.grandTotal = action.payload?.data?.grandTotal || 0;
				state.firstTimeFetchLoading = false;
				state.allocationLoading = false;
			} else {
				// Subsequent pages - append to existing data
				state.allocationData = [...state.allocationData, ...newData];
				state.isLoadingMore = false;
			}

			// Update pagination info
			state.pagination = {
				currentPage: page,
				totalPages: action.payload?.data?.totalPages || 0,
				totalRecords: action.payload?.data?.totalRecords || 0,
				limit: action.meta.arg.limit || 20,
			};
			state.hasMore = page < (action.payload?.data?.totalPages || 0);
			state.allocationError = null;
		});
		builder.addCase(
			getExpenseAllocationAction.rejected,
			(state, action: any) => {
				state.allocationLoading = false;
				state.firstTimeFetchLoading = false;
				state.isLoadingMore = false;
				state.allocationError = action.payload || action.error;
				state.allocationData = [];
				state.grandTotal = 0;
			}
		);

		// Get One-Off Expense Allocation Data
		builder.addCase(getOneOffExpenseAllocationAction.pending, (state, action) => {
			const page = action.meta.arg.page || 1;
			if (page === 1) {
				// First page - initial load
				state.firstTimeFetchLoading = true;
				state.allocationLoading = true;
			} else {
				// Subsequent pages - infinite scroll
				state.isLoadingMore = true;
			}
			state.allocationError = null;
		});
		builder.addCase(getOneOffExpenseAllocationAction.fulfilled, (state, action) => {
			const page = action.meta.arg.page || 1;
			const newData = action.payload?.data?.data || [];

			if (page === 1) {
				// First page - replace all data
				state.allocationData = newData;
				state.grandTotal = action.payload?.data?.grandTotal || 0;
				state.firstTimeFetchLoading = false;
				state.allocationLoading = false;
			} else {
				// Subsequent pages - append to existing data
				state.allocationData = [...state.allocationData, ...newData];
				state.isLoadingMore = false;
			}

			// Update pagination info
			state.pagination = {
				currentPage: page,
				totalPages: action.payload?.data?.totalPages || 0,
				totalRecords: action.payload?.data?.totalRecords || 0,
				limit: action.meta.arg.limit || 20,
			};
			state.hasMore = page < (action.payload?.data?.totalPages || 0);
			state.allocationError = null;
		});
		builder.addCase(
			getOneOffExpenseAllocationAction.rejected,
			(state, action: any) => {
				state.allocationLoading = false;
				state.firstTimeFetchLoading = false;
				state.isLoadingMore = false;
				state.allocationError = action.payload || action.error;
				state.allocationData = [];
				state.grandTotal = 0;
			}
		);
	},
});

export const { clearAllocationData, resetAllocationList } =
	expenseAllocationSlice.actions;
export default expenseAllocationSlice;
